<template>
  <section
    :dir="dir"
    class="block-products min-w-0 overflow-x-clip py-16 md:py-20"
  >
    <div class="container mx-auto min-w-0 max-w-full px-4 sm:px-6 lg:px-8">
      <header
        v-if="title || subtitle"
        class="mx-auto max-w-2xl text-center lg:text-start"
      >
        <h2
          v-if="title"
          class="text-3xl font-bold tracking-tight text-gray-900 dark:text-white md:text-4xl"
        >
          {{ title }}
        </h2>
        <p
          v-if="subtitle"
          class="mt-3 text-base leading-relaxed text-gray-600 dark:text-gray-400 md:text-lg"
        >
          {{ subtitle }}
        </p>
      </header>

      <ClientOnly v-if="cards.length">
        <div class="block-products__swiper mt-10 lg:mt-12">
          <div
            v-if="showNavigation"
            class="block-products__nav mb-8 flex items-center gap-2"
            :class="dir === 'rtl' ? 'justify-end' : 'justify-start'"
          >
            <button
              type="button"
              class="block-products__nav-btn block-products__nav-prev border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navPrevLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-heroicons-chevron-right' : 'i-heroicons-chevron-left'"
                class="size-4"
              />
            </button>
            <button
              type="button"
              class="block-products__nav-btn block-products__nav-next border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navNextLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-heroicons-chevron-left' : 'i-heroicons-chevron-right'"
                class="size-4"
              />
            </button>
          </div>
          <Swiper
            :modules="swiperModules"
            :dir="dir"
            :slides-per-view="slidesPerView"
            :space-between="spaceBetween"
            :breakpoints-base="'container'"
            :breakpoints="swiperBreakpoints"
            :pagination="paginationOptions"
            :navigation="navigationOptions"
            :autoplay="autoplayOptions"
            :loop="canLoop"
            class="equal-h-swiper block-products-swiper overflow-hidden! min-w-0 max-w-full"
          >
            <SwiperSlide
              v-for="(card, index) in cards"
              :key="index"
              class="h-auto! py-3"
            >
              <ServicePricingCard
                class="h-full"
                v-bind="card"
                :class="planSlideClass(card)"
                :builder-preview="builderPreview"
                :on-navigate="onNavigate"
              />
            </SwiperSlide>
          </Swiper>
        </div>
        <template #fallback>
          <div class="mt-10 grid grid-cols-2 items-stretch gap-3 sm:gap-5 lg:grid-cols-3">
            <ServicePricingCard
              v-for="(card, index) in cards"
              :key="index"
              v-bind="card"
            />
          </div>
        </template>
      </ClientOnly>

      <p
        v-else
        class="mt-10 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {{ emptyLabel }}
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import ServicePricingCard from '~/components/blocks/ServicePricingCard.vue'
import type { ServiceCardVariant } from '~/constants/serviceCardVariants'
import {
  type BlockProductItem,
  type ProductsLayoutMode,
  itemFeatureList,
} from '~/composables/useBlockProductsPlans'

const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    items?: BlockProductItem[]
    layout?: ProductsLayoutMode
    cardVariant?: ServiceCardVariant
    autoplay?: boolean
    autoplayDelay?: number
    loop?: boolean
    builderPreview?: boolean
    previewDevice?: 'desktop' | 'mobile'
    onNavigate?: (to: string) => void
  }>(),
  {
    items: () => [],
    layout: 'default',
    cardVariant: 'solid',
    autoplay: true,
    autoplayDelay: 5000,
    loop: true,
  },
)

const layout = computed(() => props.layout ?? 'carousel')
const swiperModules = [Pagination, Navigation, Autoplay]

const emptyLabel = computed(() =>
  pageLocale.value === 'fa' ? 'هنوز خدمتی اضافه نشده است.' : 'No services added yet.',
)

const defaultCta = computed(() =>
  pageLocale.value === 'fa' ? 'دریافت مشاوره' : 'Get consultation',
)

type CardView = {
  title?: string
  description?: string
  price?: string
  image?: string
  icon?: string
  features: string[]
  variant: ServiceCardVariant
  ctaLabel?: string
  ctaLink?: string
  ctaIcon?: string
  highlighted?: boolean
}

const cards = computed<CardView[]>(() =>
  (props.items ?? []).map((item) => ({
    title: item.name,
    description: item.description,
    price: item.price,
    image: item.image,
    icon: item.icon,
    features: itemFeatureList(item),
    variant: props.cardVariant || 'solid',
    ctaLabel: item.ctaLabel || defaultCta.value,
    ctaLink: item.href || '/booking',
    ctaIcon: 'i-lucide-phone',
    highlighted: Boolean(item.highlighted),
  })),
)

const planCount = computed(() => cards.value.length)
const useCarouselLayout = computed(() => layout.value === 'carousel')

const spaceBetween = computed(() =>
  layout.value === 'compact' ? 12 : layout.value === 'scale' ? 28 : 24,
)

const slidesPerView = computed(() => {
  if (props.previewDevice === 'mobile') return Math.min(2, Math.max(1, planCount.value))
  if (props.previewDevice === 'desktop') {
    return Math.min(3, Math.max(1, planCount.value))
  }
  return Math.min(2, Math.max(1, planCount.value))
})

const swiperBreakpoints = computed(() => {
  const max = Math.max(1, planCount.value)
  return {
    0: { slidesPerView: Math.min(2, max), spaceBetween: 10 },
    360: { slidesPerView: Math.min(2.2, max), spaceBetween: 12 },
    480: { slidesPerView: Math.min(2.5, max), spaceBetween: 14 },
    600: { slidesPerView: Math.min(2.8, max), spaceBetween: 16 },
    768: { slidesPerView: Math.min(3, max), spaceBetween: 20 },
    1200: { slidesPerView: Math.min(3, max), spaceBetween: spaceBetween.value },
  }
})

const showNavigation = computed(() => {
  if (props.previewDevice === 'mobile') return planCount.value > 1
  if (props.previewDevice === 'desktop') return planCount.value > 3
  return planCount.value > 1
})

const navigationOptions = computed(() => {
  if (!showNavigation.value) return false
  return {
    prevEl: '.block-products__nav-prev',
    nextEl: '.block-products__nav-next',
  }
})

const navPrevLabel = computed(() =>
  pageLocale.value === 'fa' ? 'اسلاید قبلی' : 'Previous slide',
)

const navNextLabel = computed(() =>
  pageLocale.value === 'fa' ? 'اسلاید بعدی' : 'Next slide',
)

const paginationOptions = computed(() => ({
  clickable: true,
  dynamicBullets: true,
}))

const autoplayDelay = computed(() => {
  const value = Number(props.autoplayDelay)
  if (!Number.isFinite(value) || value < 1000) return 5000
  return Math.min(60000, Math.round(value))
})

const canLoop = computed(() => useCarouselLayout.value && Boolean(props.loop) && planCount.value > 1)

const autoplayOptions = computed(() => {
  if (!useCarouselLayout.value || !props.autoplay || planCount.value < 2) return false
  return {
    delay: autoplayDelay.value,
    disableOnInteraction: true,
    pauseOnMouseEnter: true,
    stopOnLastSlide: !props.loop,
  }
})

function planSlideClass(card: CardView) {
  if (layout.value === 'scale' && card.highlighted) {
    return 'lg:scale-[1.03]'
  }
  return ''
}
</script>

<style scoped>
.block-products__swiper {
  pointer-events: auto;
}

.block-products-swiper :deep(.swiper-pagination) {
  position: relative !important;
  bottom: auto !important;
  margin-top: 1rem;
  width: 100%;
}

.block-products-swiper :deep(.swiper-pagination-bullet-active) {
  background-color: var(--ui-primary);
}

.block-products__nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border-width: 1px;
  border-style: solid;
  color: var(--ui-primary);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.block-products__nav-btn.swiper-button-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.block-products__nav-btn::after {
  display: none;
}

.block-products__swiper :deep(.block-products__nav-btn.swiper-button-prev),
.block-products__swiper :deep(.block-products__nav-btn.swiper-button-next) {
  position: static;
  inset: auto;
  margin: 0;
}
</style>
